import { useState } from "react";
import { Typography } from "@/components/atoms";
import { BlockedDriversTable, PreferredDriversTable } from "@/components/organisms";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGetClientOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { useAppSelector } from "@/redux/hooks";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { profileSelector } from "@/redux/slices/profile/selectors";
import { clsx } from "@/utils";

type DriversView = "preferred" | "blocked";

/**
 * Drivers tab of My Favourites. The secondary status-badge pills toggle between
 * the Preferred Drivers and Blocked Drivers tables. Preferred Drivers is gated
 * per provider by the account-management providers `add_ons.preferred_driver` flag.
 */
export const FavouriteDrivers = () => {
  const [selectedView, setSelectedView] = useState<DriversView | null>(null);

  const profile = useAppSelector(profileSelector);
  const operatorId = useAppSelector(operatorIdSelector);
  const { accountId } = useClientScope();
  const isManaged = !!accountId;
  const { data } = useGetClientOperatorsQuery();

  // The `preferred_driver` add-on is provider-level. In self-service we gate from the logged-in
  // profile's provider; in operator scope the managed client sits under the current operator-provider,
  // so we match the operators list by the active provider uuid (`operatorIdSelector` === operator `id`)
  // instead of the logged-in profile's providerCode (which an operator account doesn't carry).
  const providerCode = profile?.accountMetadata?.providerCode?.toLowerCase();
  const operator = isManaged
    ? data?.data.operators.find((item) => item.id === operatorId)
    : data?.data.operators.find((item) => item.providerCode === providerCode);
  const isPreferredDriversEnabled = operator?.addOns?.preferredDriver ?? false;

  const view: DriversView = selectedView ?? (isPreferredDriversEnabled ? "preferred" : "blocked");

  const renderPill = (label: string, pillView: DriversView, disabled = false) => {
    const isActive = view === pillView;

    return (
      <button
        type="button"
        disabled={disabled}
        aria-pressed={isActive}
        onClick={() => setSelectedView(pillView)}
        className={clsx(
          "inline-flex items-center rounded-lg px-2 py-0.5",
          disabled
            ? "cursor-not-allowed select-none bg-neutral-gray opacity-50"
            : isActive
            ? "bg-primary-light"
            : "bg-neutral-gray hover:bg-neutral-mid-gray"
        )}
      >
        <Typography variant="action" className={isActive && !disabled ? "text-primary-dark" : "text-neutral-dark-gray"}>
          {label}
        </Typography>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        {renderPill("Preferred Drivers", "preferred", !isPreferredDriversEnabled)}
        {renderPill("Blocked Drivers", "blocked")}
      </div>

      {view === "preferred" ? <PreferredDriversTable /> : <BlockedDriversTable />}
    </div>
  );
};
